import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Leaf } from "./Decor";

const QUICK_CHIPS = [
  "What products are available?",
  "Tell me about Black Turmeric",
  "Rehydration time for dried veggies?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    const next = [...messages, { role: "user", text: msg }];
    setMessages(next);
    setBusy(true);
    try {
      const r = await api.post("/ai/chat", { message: msg, history: next.slice(-9, -1) });
      setMessages([...next, { role: "assistant", text: r.data.reply }]);
    } catch (err) {
      setMessages([
        ...next,
        { role: "assistant", text: formatApiErrorDetail(err.response?.data?.detail), error: true },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="chat-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[95] w-[calc(100vw-2rem)] max-w-sm h-[480px] max-h-[70vh] bg-cream rounded-2xl shadow-2xl border border-sand flex flex-col overflow-hidden"
          >
            <div className="bg-forest text-cream px-5 py-4 flex items-center gap-3 shrink-0">
              <span className="w-9 h-9 rounded-full bg-gold/20 text-goldlight flex items-center justify-center">
                <Leaf className="w-4.5 h-4.5" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-serif font-semibold leading-tight">Farm Assistant</p>
                <p className="text-[10px] text-cream/60 uppercase tracking-[0.18em]">Powered by Gemini AI</p>
              </div>
              <button data-testid="chat-close-btn" onClick={() => setOpen(false)} aria-label="Close chat" className="p-1.5 text-cream/70 hover:text-cream">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={listRef} data-testid="chat-messages" data-lenis-prevent className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.length === 0 && (
                <div className="pt-2">
                  <p className="text-sm font-light text-muted leading-relaxed">
                    Namaste! Ask me anything about our spices, fruits, plants or dried vegetables — availability,
                    rehydration, plant care and more.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {QUICK_CHIPS.map((chip, i) => (
                      <button
                        key={chip}
                        data-testid={`chat-chip-${i}`}
                        onClick={() => send(chip)}
                        className="rounded-full border border-gold/50 bg-beige px-3.5 py-2 text-xs text-forest transition-all hover:bg-gold hover:text-nightgreen"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.role}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gold text-nightgreen rounded-br-md"
                        : m.error
                          ? "bg-red-50 text-red-800 border border-red-200 rounded-bl-md"
                          : "bg-beige text-ink rounded-bl-md"
                    }`}
                  >
                    {m.text.replace(/\*\*/g, "")}
                  </div>
                </div>
              ))}
              {busy && (
                <div data-testid="chat-typing" className="flex justify-start">
                  <div className="bg-beige rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5">
                    {[0, 1, 2].map((d) => (
                      <span
                        key={d}
                        className="w-1.5 h-1.5 rounded-full bg-forest/50 animate-bounce"
                        style={{ animationDelay: `${d * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="shrink-0 border-t border-sand p-3 flex items-center gap-2 bg-white"
            >
              <input
                data-testid="chat-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about our farm products…"
                maxLength={600}
                className="flex-1 min-w-0 rounded-full border border-sand bg-cream px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
              />
              <button
                data-testid="chat-send-btn"
                type="submit"
                disabled={busy || !input.trim()}
                aria-label="Send message"
                className="w-10 h-10 rounded-full bg-forest text-cream flex items-center justify-center transition-all hover:bg-nightgreen disabled:opacity-50 shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="chat-open-btn"
        onClick={() => setOpen(!open)}
        aria-label="Open farm assistant chat"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-5 right-4 sm:right-6 z-[95] w-14 h-14 rounded-full bg-forest text-goldlight shadow-[0_10px_35px_rgba(17,38,29,0.35)] flex items-center justify-center border border-gold/30"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>
    </div>
  );
}
